import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Outlet } from 'react-router-dom';
import AdminSidebar from '../components/admin/AdminSidebar';
import AdminHeader from '../components/admin/AdminHeader';

const AdminLayout: React.FC = () => {
  const { currentLanguage, isRTL } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('adminSidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // Save sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem('adminSidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Auto-hide on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarCollapsed(true);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const sidebarWidth = sidebarCollapsed ? 'md:w-20' : 'md:w-64';
  const contentMargin = sidebarCollapsed 
    ? (isRTL ? 'md:pr-20' : 'md:pl-20')
    : (isRTL ? 'md:pr-64' : 'md:pl-64');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" dir={isRTL ? 'rtl' : 'ltr'} lang={currentLanguage}>
      <div className={`flex flex-col md:flex-row ${contentMargin} transition-all duration-300`}>
        {/* Desktop sidebar */}
        <AdminSidebar 
          variant="desktop" 
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        {/* Mobile sidebar overlay */}
        {mobileOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            <div className="absolute inset-0 bg-black/40 transition-opacity duration-300" onClick={() => setMobileOpen(false)} />
            {isRTL ? (
              <div className="absolute inset-y-0 right-0 w-72 sm:w-80 bg-white border-l border-gray-200 shadow-xl transform transition-transform duration-300 ease-in-out">
                <AdminSidebar variant="mobile" onNavigate={() => setMobileOpen(false)} />
              </div>
            ) : (
              <div className="absolute inset-y-0 left-0 w-72 sm:w-80 bg-white border-r border-gray-200 shadow-xl transform transition-transform duration-300 ease-in-out">
                <AdminSidebar variant="mobile" onNavigate={() => setMobileOpen(false)} />
              </div>
            )}
          </div>
        )}
        <div className="flex-1 flex flex-col min-h-screen">
          <AdminHeader onMenuClick={() => setMobileOpen(true)} />
          <main className="flex-1 p-2 sm:p-3 lg:p-4">
            <div className="max-w-6xl mx-auto">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
